import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_SITE_EXPERIENCE, SiteExperienceConfig, cloneSiteExperience } from '../config/siteExperience';
import { fetchSiteExperience } from '../services/siteExperienceApi';
import { usePartnerBranding } from './PartnerBrandingContext';

interface SiteExperienceContextValue {
  config: SiteExperienceConfig;
  loading: boolean;
}

const SiteExperienceContext = createContext<SiteExperienceContextValue | undefined>(undefined);

export const SiteExperienceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { partner, loading: partnerLoading } = usePartnerBranding();
  // Menu labels and splash copy are admin-authored database text, translated
  // server-side — so a language switch has to re-fetch, not just re-render.
  const { i18n } = useTranslation();
  const [config, setConfig] = useState<SiteExperienceConfig>(() => cloneSiteExperience(DEFAULT_SITE_EXPERIENCE));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partnerLoading) return;
    let cancelled = false;
    setLoading(true);
    fetchSiteExperience(partner?.id ?? null)
      .then(data => { if (!cancelled) setConfig(data); })
      .catch(err => {
        console.warn('[SiteExperience] Using built-in defaults:', err);
        if (!cancelled) setConfig(cloneSiteExperience(DEFAULT_SITE_EXPERIENCE));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [partnerLoading, partner?.id, i18n.language]);

  const value = useMemo(() => ({ config, loading }), [config, loading]);

  // The splash is a first-paint experience. Hold the app behind the small
  // content fetch so an override never flashes as the Trusscore default.
  return (
    <SiteExperienceContext.Provider value={value}>
      {loading ? <div aria-hidden style={{ position: 'fixed', inset: 0, background: '#f8f9fa' }} /> : children}
    </SiteExperienceContext.Provider>
  );
};

export function useSiteExperience(): SiteExperienceContextValue {
  const ctx = useContext(SiteExperienceContext);
  if (!ctx) throw new Error('useSiteExperience must be used inside SiteExperienceProvider');
  return ctx;
}
